import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MuiTextField from '@mui/material/TextField';
import type { TextFieldProps } from '@mui/material/TextField';
import { tr } from '../lib/i18n';
import { forwardRef, useState } from 'react';

function slotObject(slot: unknown): Record<string, unknown> {
  return slot && typeof slot === 'object' && !Array.isArray(slot) ? { ...slot } : {};
}

/**
 * App TextField: Material 3 outlined field (shape and colors from the theme).
 * Covers the field kinds this app actually uses: text, helper/error,
 * password, multiline, search/adornments, date, disabled, read-only.
 */
export const TextField = forwardRef<HTMLDivElement, TextFieldProps>(function TextField(
  { variant = 'outlined', type, slotProps, ...props },
  ref,
) {
  const shrinkLabel = type === 'date' || type === 'time' || type === 'datetime-local';

  return (
    <MuiTextField
      ref={ref}
      variant={variant}
      type={type}
      {...props}
      slotProps={{
        ...slotProps,
        inputLabel: shrinkLabel ? { ...slotObject(slotProps?.inputLabel), shrink: true } : slotProps?.inputLabel,
      }}
    />
  );
});

/** Password field with show/hide (MUI InputAdornments). */
export const PasswordField = forwardRef<HTMLDivElement, Omit<TextFieldProps, 'type'>>(function PasswordField(
  { slotProps, ...props },
  ref,
) {
  const [show, setShow] = useState(false);

  return (
    <TextField
      ref={ref}
      {...props}
      type={show ? 'text' : 'password'}
      slotProps={{
        ...slotProps,
        htmlInput: { dir: 'ltr', ...slotObject(slotProps?.htmlInput) },
        input: {
          ...slotObject(slotProps?.input),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label={show ? tr('إخفاء كلمة المرور', 'Hide password') : tr('إظهار كلمة المرور', 'Show password')}
                onClick={() => setShow((s) => !s)}
                onMouseDown={(e) => e.preventDefault()}
                onMouseUp={(e) => e.preventDefault()}
                edge="end"
              >
                {show ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        },
      }}
    />
  );
});

export { InputAdornment };
export type { TextFieldProps };
